import React, { useState, useEffect } from 'react';
import {
  Card, Row, Col, Typography, Form, InputNumber, Button, Space, Modal,
  message, Alert, Tag, Table, Descriptions, Select, Empty, Spin
} from 'antd';
import {
  BankOutlined, WalletOutlined, InfoCircleOutlined, HistoryOutlined,
  EyeOutlined, CheckCircleOutlined, ClockCircleOutlined, ExclamationCircleOutlined,
  PlusOutlined, StarFilled
} from '@ant-design/icons';
import api from '../../utils/api';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';

const { Title, Text } = Typography;
const { Option } = Select;

// defaults — akan di-override dari API
const DEFAULT_FEE = 2500;
const DEFAULT_MIN = 100;

const statusColor = { pending: 'orange', processing: 'blue', completed: 'green', failed: 'red', cancelled: 'red' };
const statusLabel = { pending: 'Menunggu', processing: 'Diproses', completed: 'Selesai', failed: 'Gagal', cancelled: 'Dibatalkan' };
const statusIcon = {
  pending: <ClockCircleOutlined />, processing: <ClockCircleOutlined />,
  completed: <CheckCircleOutlined />, failed: <ExclamationCircleOutlined />, cancelled: <ExclamationCircleOutlined />
};

const MerchantWithdrawal = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [balance, setBalance] = useState(0);
  const [withdrawalFee, setWithdrawalFee] = useState(DEFAULT_FEE);
  const [minWithdrawal, setMinWithdrawal] = useState(DEFAULT_MIN);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [withdrawalHistory, setWithdrawalHistory] = useState([]);
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [withdrawalData, setWithdrawalData] = useState(null);
  const [detailVisible, setDetailVisible] = useState(false);
  const [selectedWithdrawal, setSelectedWithdrawal] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState(null);

  const getToken = () => localStorage.getItem('merchant_access_token');

  const fetchWithdrawalSettings = async () => {
    try {
      const res = await api.get('/merchant/withdrawals/settings', {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      if (res.data?.data) {
        setWithdrawalFee(res.data.data.fee ?? DEFAULT_FEE);
        setMinWithdrawal(res.data.data.minAmount ?? DEFAULT_MIN);
      }
    } catch { /* pakai default */ }
  };

  useEffect(() => {
    fetchBalance();
    fetchBankAccounts();
    fetchHistory();
    fetchWithdrawalSettings();
  }, []);

  const fetchBalance = async () => {
    try {
      const res = await api.get('/merchant/finance/balance-summary', {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setBalance(parseFloat(res.data.balance?.available_balance) || 0);
    } catch { setBalance(0); }
  };

  const fetchBankAccounts = async () => {
    setLoadingAccounts(true);
    try {
      const res = await api.get('/merchant/bank-accounts', {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      // Only show verified accounts for withdrawal
      setBankAccounts((res.data?.data || []).filter(a => a.is_verified));
    } catch { setBankAccounts([]); }
    finally { setLoadingAccounts(false); }
  };

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const res = await api.get('/merchant/withdrawals/history', {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setWithdrawalHistory(res.data?.withdrawals || []);
    } catch { setWithdrawalHistory([]); }
    finally { setLoading(false); }
  };

  const handleAccountChange = (id) => {
    const acc = bankAccounts.find(a => a.id === id);
    setSelectedAccount(acc || null);
  };

  const handleSubmit = (values) => {
    const acc = bankAccounts.find(a => a.id === values.bank_account_id);
    setWithdrawalData({
      bank_account_id: values.bank_account_id,
      amount: values.amount,
      fee: withdrawalFee,
      net_amount: values.amount - withdrawalFee,
      bank_name: acc?.bank_name,
      account_number: acc?.account_number,
      account_name: acc?.account_name
    });
    setConfirmVisible(true);
  };

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await api.post('/merchant/withdrawals', withdrawalData, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      message.success('Permintaan pencairan berhasil diajukan!');
      setConfirmVisible(false);
      form.resetFields();
      setSelectedAccount(null);
      fetchBalance();
      fetchHistory();
    } catch (err) {
      message.error(err.response?.data?.error || 'Gagal mengajukan pencairan');
    } finally {
      setLoading(false);
    }
  };

  const watchedAmount = Form.useWatch('amount', form);
  const netAmount = watchedAmount ? watchedAmount - withdrawalFee : 0;

  const historyColumns = [
    {
      title: 'ID Pencairan',
      dataIndex: 'withdrawal_id',
      key: 'withdrawal_id',
      render: (v) => <Text code>{v}</Text>
    },
    {
      title: 'Bank Tujuan',
      key: 'bank_info',
      render: (_, r) => (
        <div>
          <Text strong>{r.bank_name}</Text><br />
          <Text type="secondary">{r.account_number}</Text><br />
          <Text type="secondary" style={{ fontSize: 12 }}>{r.account_name}</Text>
        </div>
      )
    },
    {
      title: 'Jumlah',
      key: 'amounts',
      render: (_, r) => (
        <div>
          <Text>Rp {parseFloat(r.amount)?.toLocaleString('id-ID')}</Text><br />
          <Text type="secondary">Fee: Rp {parseFloat(r.fee)?.toLocaleString('id-ID')}</Text><br />
          <Text strong style={{ color: '#52c41a' }}>Net: Rp {parseFloat(r.net_amount)?.toLocaleString('id-ID')}</Text>
        </div>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (s) => <Tag color={statusColor[s]} icon={statusIcon[s]}>{statusLabel[s]}</Tag>
    },
    {
      title: 'Tanggal',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (v) => dayjs(v).format('DD/MM/YYYY HH:mm')
    },
    {
      title: 'Aksi',
      key: 'action',
      render: (_, r) => (
        <Button size="small" icon={<EyeOutlined />} onClick={() => { setSelectedWithdrawal(r); setDetailVisible(true); }}>
          Detail
        </Button>
      )
    }
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 24 }}>
        <Title level={2}>Withdrawal</Title>
        <Text type="secondary">Pencairan saldo ke rekening bank yang telah terdaftar dan diverifikasi</Text>
      </div>

      <Row gutter={[24, 24]}>
        {/* Saldo */}
        <Col xs={24} md={8}>
          <Card>
            <div style={{ textAlign: 'center' }}>
              <WalletOutlined style={{ fontSize: 48, color: '#52c41a', marginBottom: 16 }} />
              <Title level={4} style={{ margin: 0 }}>Saldo Tersedia</Title>
              <Title level={2} style={{ color: '#52c41a', margin: '8px 0' }}>
                Rp {balance.toLocaleString('id-ID')}
              </Title>
              <Text type="secondary">Minimum pencairan: Rp {minWithdrawal.toLocaleString('id-ID')}</Text>
            </div>
          </Card>
        </Col>

        {/* Form */}
        <Col xs={24} md={16}>
          <Card title={<Space><BankOutlined /><span>Ajukan Pencairan</span></Space>}>
            <Alert
              message={`Pencairan diproses 1-2 hari kerja. Fee Rp ${withdrawalFee.toLocaleString('id-ID')} per transaksi.`}
              type="info" showIcon style={{ marginBottom: 20 }}
            />

            {loadingAccounts ? (
              <div style={{ textAlign: 'center', padding: 32 }}><Spin /></div>
            ) : bankAccounts.length === 0 ? (
              <Empty
                description={
                  <span>
                    Belum ada rekening yang terverifikasi.<br />
                    <Text type="secondary">Daftarkan rekening terlebih dahulu di menu <strong>Rekening Bank</strong>, lalu tunggu verifikasi admin.</Text>
                  </span>
                }
              >
                <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/merchant/settings')}>
                  Daftarkan Rekening
                </Button>
              </Empty>
            ) : (
              <Form form={form} layout="vertical" onFinish={handleSubmit}>
                <Form.Item
                  name="bank_account_id"
                  label="Rekening Tujuan"
                  rules={[{ required: true, message: 'Pilih rekening tujuan' }]}
                >
                  <Select
                    placeholder="Pilih rekening bank terdaftar"
                    size="large"
                    onChange={handleAccountChange}
                  >
                    {bankAccounts.map(acc => (
                      <Option key={acc.id} value={acc.id}>
                        <Space>
                          {acc.is_primary ? <StarFilled style={{ color: '#1677ff' }} /> : <BankOutlined />}
                          <span><strong>{acc.bank_name}</strong> — {acc.account_number} ({acc.account_name})</span>
                        </Space>
                      </Option>
                    ))}
                  </Select>
                </Form.Item>

                {selectedAccount && (
                  <Card size="small" style={{ marginBottom: 16, background: '#f0f5ff' }}>
                    <Descriptions size="small" column={1}>
                      <Descriptions.Item label="Bank">{selectedAccount.bank_name}</Descriptions.Item>
                      <Descriptions.Item label="Nomor Rekening">
                        <Text style={{ fontFamily: 'monospace' }}>{selectedAccount.account_number}</Text>
                      </Descriptions.Item>
                      <Descriptions.Item label="Nama Pemilik">{selectedAccount.account_name}</Descriptions.Item>
                    </Descriptions>
                  </Card>
                )}

                <Form.Item
                  name="amount"
                  label="Jumlah Pencairan"
                  rules={[
                    { required: true, message: 'Masukkan jumlah' },
                    { type: 'number', min: minWithdrawal, message: `Minimum Rp ${minWithdrawal.toLocaleString('id-ID')}` },
                    { type: 'number', max: balance, message: `Melebihi saldo tersedia (Rp ${balance.toLocaleString('id-ID')})` }
                  ]}
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    size="large"
                    formatter={v => `Rp ${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    parser={v => v.replace(/Rp\s?|(,*)/g, '')}
                    placeholder={`Minimum Rp ${minWithdrawal.toLocaleString('id-ID')}`}
                  />
                </Form.Item>

                {watchedAmount > 0 && (
                  <Card size="small" style={{ marginBottom: 16, background: '#f6ffed' }}>
                    <Row justify="space-between">
                      <Col><Text>Jumlah Pencairan</Text><br /><Text>Fee</Text><br /><Text strong>Diterima</Text></Col>
                      <Col style={{ textAlign: 'right' }}>
                        <Text>Rp {watchedAmount.toLocaleString('id-ID')}</Text><br />
                        <Text>Rp {withdrawalFee.toLocaleString('id-ID')}</Text><br />
                        <Text strong style={{ color: '#52c41a' }}>Rp {netAmount.toLocaleString('id-ID')}</Text>
                      </Col>
                    </Row>
                  </Card>
                )}

                <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                  <Button type="primary" htmlType="submit" size="large" loading={loading}>
                    Ajukan Pencairan
                  </Button>
                </Form.Item>
              </Form>
            )}
          </Card>
        </Col>
      </Row>

      {/* History */}
      <Card title={<Space><HistoryOutlined /><span>Riwayat Pencairan</span></Space>} style={{ marginTop: 24 }}>
        <Table
          columns={historyColumns}
          dataSource={withdrawalHistory}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10, showTotal: (t) => `Total ${t} pencairan` }}
        />
      </Card>

      {/* Confirm Modal */}
      <Modal
        title="Konfirmasi Pencairan"
        open={confirmVisible}
        onCancel={() => setConfirmVisible(false)}
        footer={
          <Space>
            <Button onClick={() => setConfirmVisible(false)}>Batal</Button>
            <Button type="primary" loading={loading} onClick={handleConfirm}>Konfirmasi</Button>
          </Space>
        }
      >
        {withdrawalData && (
          <>
            <Alert
              message="Pastikan data sudah benar sebelum konfirmasi"
              type="warning" showIcon style={{ marginBottom: 16 }}
            />
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="Bank">{withdrawalData.bank_name}</Descriptions.Item>
              <Descriptions.Item label="Nomor Rekening">
                <Text style={{ fontFamily: 'monospace' }}>{withdrawalData.account_number}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Nama Pemilik">{withdrawalData.account_name}</Descriptions.Item>
              <Descriptions.Item label="Jumlah">Rp {parseFloat(withdrawalData.amount)?.toLocaleString('id-ID')}</Descriptions.Item>
              <Descriptions.Item label="Fee">Rp {parseFloat(withdrawalData.fee)?.toLocaleString('id-ID')}</Descriptions.Item>
              <Descriptions.Item label="Diterima">
                <Text strong style={{ color: '#52c41a' }}>Rp {parseFloat(withdrawalData.net_amount)?.toLocaleString('id-ID')}</Text>
              </Descriptions.Item>
            </Descriptions>
          </>
        )}
      </Modal>

      {/* Detail Modal */}
      <Modal
        title="Detail Pencairan"
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={<Button onClick={() => setDetailVisible(false)}>Tutup</Button>}
      >
        {selectedWithdrawal && (
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="ID"><Text code>{selectedWithdrawal.withdrawal_id}</Text></Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={statusColor[selectedWithdrawal.status]} icon={statusIcon[selectedWithdrawal.status]}>
                {statusLabel[selectedWithdrawal.status]}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Bank">{selectedWithdrawal.bank_name}</Descriptions.Item>
            <Descriptions.Item label="Nomor Rekening">{selectedWithdrawal.account_number}</Descriptions.Item>
            <Descriptions.Item label="Nama Pemilik">{selectedWithdrawal.account_name}</Descriptions.Item>
            <Descriptions.Item label="Jumlah">Rp {parseFloat(selectedWithdrawal.amount)?.toLocaleString('id-ID')}</Descriptions.Item>
            <Descriptions.Item label="Fee">Rp {parseFloat(selectedWithdrawal.fee)?.toLocaleString('id-ID')}</Descriptions.Item>
            <Descriptions.Item label="Diterima">
              <Text strong style={{ color: '#52c41a' }}>Rp {parseFloat(selectedWithdrawal.net_amount)?.toLocaleString('id-ID')}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Tgl Pengajuan">{dayjs(selectedWithdrawal.created_at).format('DD/MM/YYYY HH:mm')}</Descriptions.Item>
            {selectedWithdrawal.processed_at && (
              <Descriptions.Item label="Tgl Diproses">{dayjs(selectedWithdrawal.processed_at).format('DD/MM/YYYY HH:mm')}</Descriptions.Item>
            )}
            {selectedWithdrawal.notes && (
              <Descriptions.Item label="Catatan"><Text type="secondary">{selectedWithdrawal.notes}</Text></Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default MerchantWithdrawal;
